import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useGetOnboardingChecklistQuery, useUndismissOnboardingMutation } from 'store/services/onboarding/onboardingApi';

// The "Finish setup" profile-menu item (Asana 1218957945501621, Decision 17):
// shown while the Setup Banner is dismissed and at least one reward is not
// claimed; clicking clears the dismissal and goes Home. One hook, two menus
// (the desktop dropdown and the phone slide-out), so they cannot disagree.

export interface FinishSetupState {
  /** Render the item */
  visible: boolean;
  /** Points not yet claimed across every task */
  remainingPoints: number;
  busy: boolean;
  /** Undismiss, then navigate Home; resolves after navigation */
  finishSetup: () => Promise<void>;
}

export const useFinishSetup = (enabled: boolean = true): FinishSetupState => {
  const navigate = useNavigate();
  const { data } = useGetOnboardingChecklistQuery(undefined, { skip: !enabled });
  const [undismiss, { isLoading }] = useUndismissOnboardingMutation();

  const remainingPoints = useMemo(
    () => (data?.tasks ?? []).filter((t) => !(t.status === 'COMPLETED' && t.pointsAwarded)).reduce((sum, t) => sum + t.pointsReward, 0),
    [data]
  );
  const visible = !!data?.onboardingDismissedAt && remainingPoints > 0;

  const finishSetup = useCallback(async () => {
    try {
      await undismiss().unwrap();
    } catch {
      toast.error('Could not bring the setup card back. Please try again.');
      return;
    }
    navigate('/home');
  }, [undismiss, navigate]);

  return { visible, remainingPoints, busy: isLoading, finishSetup };
};

export default useFinishSetup;
